import {
	FormEvent,
	MouseEvent, useEffect, useMemo, useState, type ReactNode
} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faEye, faInfoCircle, faShoppingBasket, faSyncAlt
} from '@fortawesome/free-solid-svg-icons';

import {
	List,
	TextFilter,
	Button,
	Dropdown,
} from '@abm-international/react-components';
import Api, { comocatOrder, logout } from '../../libraries/api';
import useVisits from '../../api/comocat/useVisits';
import Modal from '../../components/modal/modal';
import { VisitsNotes } from './VisitsInfo';
import { Visit } from '../../types/visits';
import { WeekdaySelector, getWeekdayLabel } from './WeekdaySelector/WeekdaySelector';
import api from '../../libraries/api';
import { toast } from 'react-toastify';
import { VisitsInfo } from './VisitsInfo/VisitsInfo';
import './Visits.scss';
import { CRUDField, CRUDFieldTypes } from '../Settings/Permissions/CRUD/crud';
import { Spinner } from '@abm-international/react-components';
import { useRestrictedContent } from '../../libraries/hooks';
import ForbiddenError from '../../components/Error/ForbiddenError/ForbiddenError';
import { ConfirmDialog } from '../../components/ConfirmDialog/ConfirmDialog';
import config from '../../config';
import CommocatButton from '../../components/CommocatButton';
import { appNavigationToggleCollapse, comocatVisitsToggleCollapse } from '../appActions';
import { useDispatch, useSelector } from 'react-redux';
import { selectIsComocatVisitsCollapsed } from '../selectors';
import PdfModal from '../../components/PdfModal/PdfModal';
import dayjs from 'dayjs';
import 'dayjs/locale/nl';
import { t } from '../../libraries/i18n';
import { tLocal } from '../../libraries/i18nLocal';




enum VisitStatus {
    None = 0,
    ToDo = 1,
    Ordered = 2,
    Later = 3,
    OnHold = 4,
    Moved = 5,
    Service = 6,
    ViaPortal = 7,
    _8 = 8,
    _9 = 9,
}

const keyMapper: { [index: number]: VisitStatus } = {
    // 68 | D
    // 68: VisitStatus.ToDo,
    // 71 | G
    71: VisitStatus.None,
    // 76 | L
    76: VisitStatus.Later,
    // 83 | S
    83: VisitStatus.Service,
};

const getVisitStatusLabel = (status: number | string): string => {
    const statusKeyMap: Record<number, string> = {
        0: 'status_geen',
        1: 'status_te_doen',
        2: 'status_besteld',
        3: 'status_later',
        4: 'status_on_hold',
        5: 'status_verplaatst',
        6: 'status_service',
        7: 'status_via_portal',
    };
    const key = statusKeyMap[Number(status)];
    return key ? tLocal(`comocat.visits.${key}`) : String(status);
};

const statuses: { value: string; display: ReactNode; hidden?: boolean }[] = [
    { value: '0', display: <span><span className="underline">{tLocal('comocat.visits.status_geen')[0]}</span>{tLocal('comocat.visits.status_geen').slice(1)}</span> },
    { value: '1', display: tLocal('comocat.visits.status_te_doen') },
    { value: '2', display: tLocal('comocat.visits.status_besteld') },
    { value: '3', display: <span><span className="underline">{tLocal('comocat.visits.status_later')[0]}</span>{tLocal('comocat.visits.status_later').slice(1)}</span> },
    { value: '4', display: tLocal('comocat.visits.status_on_hold') },
    { value: '5', display: tLocal('comocat.visits.status_verplaatst') },
    { value: '6', display: <span><span className="underline">{tLocal('comocat.visits.status_service')[0]}</span>{tLocal('comocat.visits.status_service').slice(1)}</span> },
    { value: '7', display: tLocal('comocat.visits.status_via_portal') },
];

const changeableStatuses: VisitStatus[] = [0, 1, 3, 6];

const ddlStatuses = statuses.filter(s => changeableStatuses.indexOf(Number(s.value)) > -1);


export const formatCurrency = (amount: number) => {
	return `€ ${amount.toLocaleString('nl-BE', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})}`;
};

export const basicSort = (col: string) => {
	return (a: any, b: any) => {
		const keys = col.split('.');
		let itemValueA = a;
		let itemValueB = b;

		keys.forEach(key => {
			itemValueA = itemValueA?.[key];
			itemValueB = itemValueB?.[key];
		});

		return itemValueA > itemValueB ? 1 : -1;
	};
};

export const basicFilter = (key: string) => {
	return (item: any, value: any) => {
		const keys = key.split('.');
		let itemValue = item;

		keys.forEach(key => {
			itemValue = itemValue?.[key];
		});

		return itemValue ? String(itemValue).toLocaleLowerCase().includes(String(value).toLocaleLowerCase()) : true;
	};
};




export const Visits = (props:any) => {
	const { visits: _visits } = useVisits();
	const [RestrictedContent, RestrictedContentError] = useRestrictedContent(props.requiredPermission);
	const [openModalInfo, setOpenModalInfo] = useState<Visit>();
	const [openModalCiao, setOpenModalCiao] = useState(false);
	const [activeLine, setActiveLine] = useState<Visit>();
	const [selectedWeekdays, setSelectedWeekdays] = useState<number[]>([new Date().getDay() * 10]);
	//const [openOrders, setOpenOrders] = useState(false);
	const [showConfirm, setShowConfirm] = useState(false);
	const [selectedCustomer, setSelectedCustomer] = useState<Visit | undefined>(undefined);
	const statusOkay = [2, 4, 5, 6];
	const [visitsNotOkay, setVisitsNotOkay] = useState<Visit[]>(_visits);
	const [activeView, setActiveView] = useState<'visits' | 'customer' |'orders'>('visits');
	const dispatch = useDispatch();
	const isComocatVisitsCollapsed = useSelector(selectIsComocatVisitsCollapsed);
	const [notesCollapsed, setNotesCollapsed] = useState(isComocatVisitsCollapsed);
	const [visitsData, setVisitsData] = useState<Visit[]>([]);
	const [runningOrders, setRunningOrders] = useState<any[]>([]);

	const [pdfModal, setPdfModal] = useState<{ open: boolean, url?: string, downloadUrl?: string }>({ open: false });
	const renderPdfModal = () => (
		<PdfModal
			key="pdf-modal-running-orders"
			file={pdfModal.url}
			downloadUrl={pdfModal.downloadUrl}
			close={() => setPdfModal({ open: false })}
		/>
	);

	const openPdfModal = (customerId: string|number, orderId: string|number) => {
		if (!orderId || !customerId) return null;
		const url = `${process.env.REACT_APP_PORTAL_URL}/api/customer.order.history/${customerId}/orders/${orderId}/pdf`;
		setPdfModal({
			open: true,
			url: url,
			downloadUrl: `${url}?download`
		});
	};
	useEffect(() => {
		const fetchRunningOrders = async () => {
			const runningOrders = await api.getRunningOrders();
			const parsedRunningOrders = runningOrders.map((order: any) => {
				return {
					...order,
					customerId: order.id,
					id: order.rowId
				};
			}); // kan weg wnr we backend aapassen maar moest backword compatibel zijn
			setRunningOrders(parsedRunningOrders);
		};
		fetchRunningOrders();
	}, []);

	useEffect(() => {
		setVisitsData(_visits);
	}, [_visits]);

	// useEffect(() => {
	// 	dispatch(appNavigationToggleCollapse(true));
	// }, [dispatch]);


	const columns = useMemo(() => {
		let _columns:{key:number, title:string, render?:Function, sort?:Function, filter?:{
            test:Function, component:CRUDFieldTypes
        }}[] = [

        	{
        		key: 2,
        		title: tLocal('comocat.visits.col_number'),
        		render: ({ id }: { id: number }) => id,
        		sort: basicSort('id'),
        		filter: {
        			test: basicFilter('id'),
        			component: TextFilter
        		}
        	},
        	{
        		key: 3,
        		title: tLocal('comocat.visits.col_name'),
        		render: ({ name }: { name: string }) => <span >{name}</span>,
        		// sort: basicSort('name'),
        		filter: {
        			test: basicFilter('name'),
        			component: TextFilter
        		}
        	},

        ];
		if (activeView === 'visits') {
			_columns=[
				//     {
			// 	key: 1,
			// 	title: '#',
			// 	render: ({ sequence }: { sequence: any }) => <span className="table-text-ellipsis">{sequence}</span>,
			// 	sort: basicSort('sequence')
			// },
				..._columns,
				{
        		key: 4,
        		title: tLocal('comocat.visits.col_city'),
        		render: ({ address }: { address: any }) => <span className="table-text-ellipsis">{address.city}</span>,
        		// sort: basicSort('address.city'),
        		filter: {
        			test: basicFilter('address.city'),
        			component: TextFilter
        		}
        	},
				// {
				// 	key: 5,
				// 	title: 'Saldo',
				// 	render: ({ saldo }: { saldo: number }) => formatCurrency(saldo),
				// },
				{
					key: 6,
					title: tLocal('comocat.visits.col_status'),
					render: (item: any) => item.ror_toe ? <Dropdown
						className='status-ddl'
						items={[
							...ddlStatuses,
							...(statuses.findIndex(s => s.value === item.status) > -1 ? [{ value: item.status, display: statuses.find(s => String(s.value) === String(item.status))?.display, hidden: true }] : [])
						]}
						selected={item.status || '1'}
						onSelect={(status: string) => changeStatus(item, status)}
						color={getStatusColor(item.status)}
					/>:<span>{tLocal('comocat.visits.status_niet_ingepland')}</span>,
					sort: basicSort('status'),
				},
				{
					key: 7,
					title: '',
					render: (item: Visit) => <>
						<div style={{
							display: 'flex', flexFlow: 'row nowrap', gap: '0.85rem', justifyContent: 'flex-end', alignItems: 'center'
						}}>
							<div>
								<CommocatButton primary color={!['2', '6'].includes(item.status) ? 'green' : 'gray'} className="doen-btn" onClick={(e: MouseEvent<HTMLButtonElement>) => loginComocat(e, item)}>
									{tLocal('comocat.visits.btn_doen')}
								</CommocatButton>
							</div>

							<div style={{
								display: 'flex', flexFlow: 'column ', justifyContent: 'flex-end', alignItems: 'center'
							}}>
								<Button className="littleIcons" onClick={(e: MouseEvent<HTMLButtonElement>) => updateModalInfo(e, item)}>
									<FontAwesomeIcon icon={faInfoCircle} />
								</Button>
								<Button className={`littleIcons ${item.openOrders ? 'openOrder' : ''}`} onClick={() => {
									setShowConfirm(true);
									setSelectedCustomer(item);
								}}>
									<FontAwesomeIcon icon={faShoppingBasket} />
								</Button>

							</div>

						</div>
					</>,
				}, ];
		}
		if (activeView === 'customer') {
			 _columns=[ {
				key: 1,
				title: tLocal('comocat.visits.col_vtw_tour'),
				render: ({ ror_toe }: { ror_toe: string }) => !ror_toe ? tLocal('comocat.visits.col_not_on_tour') : [...new Set(String(ror_toe)?.split(',')?.map(Number))].sort((a, b) => a - b).map(day => getWeekdayLabel(day)).join(' '),
				//sort: basicSort('day')
			}, ..._columns, {
        		key: 4,
        		title: tLocal('comocat.visits.col_city'),
        		render: ({ address }: { address: any }) => <span className="table-text-ellipsis">{address.city}</span>,
        		sort: basicSort('address.city'),
        		filter: {
        			test: basicFilter('address.city'),
        			component: TextFilter
        		}
        	}, {
				key: 5,
				title: tLocal('comocat.visits.col_phone'),
				render: ({ phone }: { phone: string }) => <span>{phone}</span>,
			},
			// {
			// 	key: 6,
			// 	title: 'BD',
			// 	render: ({ BD }: { BD: string }) => BD,
			// },
			{
				key: 7,
				title: tLocal('comocat.visits.col_article_restriction'),
				render: ({ orderRestrictions }: { orderRestrictions: string }) => orderRestrictions,
			}
			];
		}
		if (activeView === 'orders') {
			_columns=[{
        		key: 2,
        		title: tLocal('comocat.visits.col_number'),
        		render: ({ customerId }: { customerId: number }) => customerId,
        		// sort: basicSort('customerId'),
        		filter: {
        			test: basicFilter('customerId'),
        			component: TextFilter
        		}
        	},
        	{
        		key: 3,
        		title: tLocal('comocat.visits.col_name'),
        		render: ({ name }: { name: string }) => <span >{name}</span>,
        		// sort: basicSort('name'),
        		filter: {
        			test: basicFilter('name'),
        			component: TextFilter
        		}
        	}, {
				key: 4,
				title: tLocal('comocat.visits.col_delivery'),
				render: ({ deliveryDay }: { deliveryDay: string }) => deliveryDay,
                	// sort: basicSort('deliveryDay'),
				filter: {
        			test: basicFilter('deliveryDay'),
        			component: TextFilter
        		}
			}, {
				key: 5,
				title: tLocal('comocat.visits.col_time'),
				render: ({ OrderDayTime }: { OrderDayTime: string }) => OrderDayTime
			}, {
				key: 6,
				title: tLocal('comocat.visits.col_origin'),
				render: ({ origin }: { origin: string }) => origin,
				filter: {
        			test: basicFilter('origin'),
        			component: TextFilter
        		},
			}, {
				key: 7,
				title: tLocal('comocat.visits.col_amount'),
				render: ({ orderAmount }: { orderAmount: number }) => formatCurrency(orderAmount)
			}, {
				key: 8,
				title: tLocal('comocat.visits.col_status'),
				render: ({ status }: { status: string }) => { return t(`bestel.bestel_status_${status}`); }
			},
			{
				key: 9,
				title: '',
				render: (openOrder:any) => (
					<div>
						{/* <CommocatButton primary color="green" className="doen-btn" onClick={() => openPdfModal(openOrder.id, openOrder.rowId)}>
							Bekijken
						</CommocatButton> */}
						<FontAwesomeIcon onClick={() => openPdfModal(openOrder.id, openOrder.rowId)} icon={faEye} style={{ color: '#1C9462', cursor: 'pointer', fontSize: '2rem' }} title={tLocal('comocat.visits.view_order_title')} />
					</div>
				)
			} ];
		}
		return _columns;
	}, [visitsData, activeView]);


	const visits = useMemo(() => {
		let _visitRows = visitsData;
		// if (openOrders) {
		// 	_visitRows = _visitRows?.filter((visit: Visit) => !!Number(visit.openOrders));
		// }
		if (activeView === 'customer') {

			return _visitRows?.reduce((acc: any, visit: Visit) => {
				const index = acc.findIndex((v: Visit) => v.id === visit.id);
				if (index === -1) acc.push(visit);
				else acc[index].ror_toe += `,${visit.ror_toe}`;
				return acc;
			}, []);
		}

		if (selectedWeekdays.length && selectedWeekdays.indexOf(99) === -1) {
			_visitRows = _visitRows?.filter((visit: Visit) => selectedWeekdays.indexOf(visit.ror_toe) > -1);
		}
		if (selectedWeekdays.length && selectedWeekdays.indexOf(99) > -1) {
			_visitRows = _visitRows?.filter((visit: Visit) => !visit.ror_toe);
		}

		return _visitRows;
	}, [visitsData, selectedWeekdays, activeView]);

	const OnCiao = () => {
		setVisitsNotOkay(_visits?.filter((v: Visit) => {
			return v.ror_toe === new Date().getDay() * 10 && statusOkay.indexOf(Number(v.status)) === -1;
		}));
		// if (visitsNotOkay?.length === 0) {
		// 	logout();
		// 	window.location.href = config.PORTAL_LOGIN_URL || 'test';
		// 	toast.success(tLocal('comocat.visits.toast_ciao_success'));
		// 	return;
		// }
		setOpenModalCiao(true);
	};


	 const loginComocat = async (e: MouseEvent<HTMLButtonElement>, visit: Visit) => {
		e.stopPropagation();
		await Api.setCustomer(visit.id, visit);
		await comocatOrder(true);
		window.document.location = '/order';
	};

	const updateModalInfo = (e: MouseEvent<HTMLButtonElement>, item: Visit) => {
		e.stopPropagation();
		setOpenModalInfo(item);
	};

	const updateActiveLine = (e?: MouseEvent<HTMLDivElement>, line?: any) => {
		if (!e) return;
		if (String(e?.target) === '[object HTMLDivElement]' || String(e?.target) === '[object HTMLBodyElement]') {
			e?.stopPropagation();
			setOpenModalInfo(undefined);
		}
		if (activeView === 'customer' && line) {
			const days = line.ror_toe ? [...new Set(String(line.ror_toe).split(',').map(Number))].sort((a, b) => a - b) : [];
			const todayCode = new Date().getDay() * 10;
			const targetDay = days.includes(todayCode) ? todayCode : (days.length > 0 ? days[0] : null);
			const foundLine = visitsData.find(v => v.id === line.id && (targetDay !== null ? Number(v.ror_toe) === targetDay : !v.ror_toe));
			if (targetDay !== null) {
				setSelectedWeekdays([targetDay]);
			} else {
				setSelectedWeekdays([99]);
			}
			setActiveView('visits');
			setActiveLine(foundLine);
			return;
		}
		setActiveLine(line);
	};

	const changeStatus = async (item: Visit, status: string) => {
		const oldStatus = item.status;

		try {


			const x = await api.post(`/api/v1/comocat/customers/${item.id}/status`, JSON.stringify({
				...item
				, status: status
			}));


			setVisitsData(prev => {
				const newVisits = [...prev];
				const index = prev.findIndex(visit => { return visit.id === item.id && visit.ros_jaw === item.ros_jaw && visit.ror_toe === item.ror_toe && visit.sequence === item.sequence; });//todo doe alles op die guid
				if (index > -1) {
					newVisits[index].status = status;

					if (x?.extraStatus && x?.extraStatus?.ros_sta === '5') {
						const existsIndex = newVisits?.findIndex(visit => {
							return visit.id === x?.extraStatus?.ros_klt_rek
                            // && visit.ros_jaw === x?.extraStatus?.ros_jaw
                            && visit.ror_toe === x?.extraStatus?.ros_toe
                            && visit.status == x?.extraStatus?.ros_sta;
						});
						if (existsIndex > -1) {
							newVisits[existsIndex].status = x?.extraStatus?.ros_sta;
							newVisits[existsIndex].sequence = x?.extraStatus?.ros_vlg;
							newVisits[existsIndex].ror_toe = x?.extraStatus?.ros_toe;
							newVisits[existsIndex].ros_jaw = x?.extraStatus?.ros_jaw;
						} else {
					    	newVisits.push({
								...item,
								status: x?.extraStatus?.ros_sta,
								sequence: x?.extraStatus?.ros_vlg,
								ror_toe: x?.extraStatus?.ros_toe,
								ros_jaw: x?.extraStatus?.ros_jaw,
							});
						}
					}
				}
				const returnValue = newVisits;
				return returnValue;
			});
			toast.success(tLocal('comocat.visits.toast_status_success', item.name, String(item.id), getVisitStatusLabel(Number(item.status))));

		} catch (e) {
			setVisitsData(prev => {
				const newVisits = [...prev];
				const index = prev.findIndex(visit => { return visit.id === item.id && visit.ros_jaw === item.ros_jaw && visit.ror_toe === item.ror_toe; });
				if (index > -1) {
					newVisits[index].status = oldStatus;
				}

				return newVisits;
			});

			toast.error(tLocal('comocat.visits.toast_status_error', item.name, String(item.id)));
		}
	};

	const onKeyPress = async (key: number, item: Visit) => {
		// blokkeer als een input/textarea actief is
		// if (activeView !== 'visits' ) return;
		// console.log('key pressed:', activeView);
		if (document.activeElement && (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'TEXTAREA')) return;
		if (key === 68) {
			await Api.setCustomer(item.id, item);
			await comocatOrder(true);
			window.document.location = '/order';
			return;
		}
		const newStatus = keyMapper[key];
		if (newStatus === undefined) return;
		changeStatus(item, String(newStatus));
	};

	const getStatusColor = (status: number | string | null | undefined) => {
		const s = status == null ? null : Number(status);
		if (s === null || s === 1) return '#DB5656';
		if ([2, 6, 0].includes(s)) return '#1c9462';
		if ([0, 3, 4, 6].includes(s)) return '#FF9F1C';
		return 'black';
	};


	const [ciaoLoading, setCiaoLoading] = useState(false);
	const [orderDayFilter, setOrderDayFilter] = useState<string>(dayjs().format('DD-MM-YYYY'));

	const orderDayOptions = useMemo(() => {
	    const days = [];
	    for (let i = 0; i <= 14; i++) {
	        days.push(dayjs().subtract(i, 'day').format('YYYY-MM-DD'));
	    }
	    return days;
	}, []);

	const filteredRunningOrders = useMemo(() => {
	    if (!runningOrders) return [];
	    return runningOrders.filter((order: any) => {
	        if (!order.OrderDayTime) return [];
			return order.OrderDayTime?.split(' ')[0]?.trim() === orderDayFilter?.trim();

	    });
	}, [runningOrders, orderDayFilter]);

	//scroll naar actieve lijn bij verandering
	useEffect(() => {
		if (!activeLine || !visits || activeView !== 'visits') return;
		const index = visits.findIndex(v => v.id === activeLine.id);
		if (index === -1) return;
		const el = document.querySelectorAll('.list__item')[index];
		if (el) {
			el.scrollIntoView({ behavior: 'smooth', block: 'center' });
		}
	}, [activeLine, visits]);

	return (
		<>
			<RestrictedContentError>
				<ForbiddenError permissionName={tLocal('comocat.visits.forbidden_reps_only')} />
			</RestrictedContentError>
			<RestrictedContent>
				<Modal
					show={!!openModalInfo}
					close={(e: MouseEvent<HTMLDivElement>) => { e.stopPropagation(); setOpenModalInfo(undefined); }}
					children={<VisitsInfo visit={openModalInfo} />}
				/>
				<ConfirmDialog
					open={openModalCiao}
					title={ciaoLoading ? tLocal('comocat.visits.ciao_title_loading') : tLocal('comocat.visits.ciao_title_irregularities')}
					message={
						ciaoLoading ? (
							<div style={{
								display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', minHeight: 80
							}}>
								<div className="spinner" style={{
									width: 32, height: 32, border: '4px solid #eee', borderTop: '4px solid #1C9462', borderRadius: '50%', animation: 'spin 1s linear infinite'
								}} />
								<div>{tLocal('comocat.visits.ciao_auto_logout')}</div>
							</div>
						) : (
							visitsNotOkay?.length === 0 ? tLocal('comocat.visits.ciao_no_irregularities') : (
								<div>
									<div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
										<p>{tLocal('comocat.visits.ciao_not_ok_intro')}</p>
										{visitsNotOkay?.map((v: Visit) => (
											<div style={{
												display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '4px'
											}} key={v.id} >
												<div style={{ fontWeight: 'bold' }}>{v.id}</div>
												<div>{v.name?.toLowerCase()} </div>
											</div>
										))}
									</div>
									<p>{tLocal('comocat.visits.ciao_confirm_close')}</p>
								</div>
							)
						)
					}
					confirmLabel={ciaoLoading ? '' : tLocal('comocat.visits.btn_confirm')}
					cancelLabel={ciaoLoading ? '' : tLocal('comocat.visits.btn_cancel')}
					onConfirm={async () => {
						setCiaoLoading(true);
						setTimeout(async () => {
							const telegram = await api.postCiao();
							setOpenModalCiao(false);
							setCiaoLoading(false);
							logout();
							window.location.href = config.PORTAL_LOGIN_URL || 'test';
							toast.success(tLocal('comocat.visits.toast_ciao_success'));
						}, 2000);
					}}
					onCancel={() => { if (!ciaoLoading) setOpenModalCiao(false); }}
				/>

				<div className="Visits">
					{/* <h1>Toeren</h1> */}
					<div className='button-group'>
						<Button
							primary={activeView === 'visits'}
							color={activeView === 'visits' ? 'green' : 'gray'}
							onClick={() => setActiveView('visits')}
						>
                            {tLocal('comocat.visits.tab_visits')}
						</Button>
						<Button
							primary={activeView === 'customer'}
							color={activeView === 'customer' ? 'green' : 'gray'}
							onClick={() => setActiveView('customer')}
						>
                        {tLocal('comocat.visits.tab_customers')}
						</Button>
						<Button
							primary={activeView === 'orders'}
							color={activeView === 'orders' ? 'green' : 'gray'}
							onClick={() => setActiveView('orders')}
						>
                            {tLocal('comocat.visits.tab_running_orders')}
						</Button>
					</div>

					<div className='visits-wrapper'>
						{activeView === 'visits' &&
						<div className='visits-header'>
							<div className='left'>
								<WeekdaySelector
									// multiple
									selected={selectedWeekdays}
									items={[{ value: 99, display: tLocal('comocat.visits.filter_other') }]}
									onChange={setSelectedWeekdays}
								/>
								<br />
							</div>
							<div className='right'>
								<p>{tLocal('comocat.visits.weekday_done_question')} </p>
								<Button
									primary
									color="green"
									onClick={OnCiao}
								>
                                    {tLocal('comocat.visits.btn_ciao')}
								</Button>
							</div>
						</div>}

						{activeView === 'orders' &&<>
							{pdfModal.open && renderPdfModal()}
							<div className="order-day-filter-row">

                        	<label htmlFor="order-day-filter" className="order-day-filter-label">{tLocal('comocat.visits.order_day_label')}</label>
                        	<select
                        		id="order-day-filter"
                        		value={orderDayFilter}
                        		onChange={e => setOrderDayFilter(e.target.value)}
                        		className="order-day-filter-select"
                        	>
                        		{orderDayOptions.map(day => (
                        			<option key={day} value={dayjs(day).locale('nl').format('DD-MM-YYYY')}>{dayjs(day).locale('nl').format('dddd DD-MM-YYYY')}</option>
                        		))}
                        	</select>
							</div>
						</>
						}

						<div className='visits-holder'>
							{activeView === 'customer' &&
							<List
								className={'comocat visits-list customer-view'}
								action={updateActiveLine}
								header={columns}
								content={visits}
								selectedExternalItem={activeLine}
							/>}

							{activeView === 'visits' &&
							<List
								onKeyPress={onKeyPress}
								className={'comocat visits-list visits-view'}
								action={updateActiveLine}
								header={columns}
								content={visits}
								selectedExternalItem={activeLine}
							/>}
							{activeView === 'orders' &&
							<List
							 key={orderDayFilter}
							 onKeyPress={onKeyPress}
							 className={'comocat visits-list orders-view'}
							 action={updateActiveLine}
							 header={columns}
							 content={filteredRunningOrders}
							 selectedExternalItem={activeLine}
							/>

							}
							{activeView === 'visits' && (
                                notesCollapsed ? (
                                    <div
                                    	className="visits-notes-collapsed-bar"
                                    	role="button"
                                    	aria-label="Expand notes"
                                    	onClick={() => { dispatch(comocatVisitsToggleCollapse(notesCollapsed));
                                    		setNotesCollapsed((notesCollapsed: boolean) => !notesCollapsed); }}
                                    >
                                    	<span className="visits-notes-arrow-collapsed">&#x25B6;</span>
                                    </div>
                                ) : (
                                    <div className="visits-notes-expanded-row">
                                    	<div
                                    		className="visits-notes-collapsed-bar"
                                    		role="button"
                                    		aria-label="Expand notes"
                                    		onClick={() => setNotesCollapsed((notesCollapsed: boolean) => !notesCollapsed)}
                                    	>
                                    		<span className="visits-notes-arrow-expanded">&#x25B6;</span>
                                    	</div>
                                    	<VisitsNotes visit={activeLine} />
                                    </div>
                                )
							)}
						</div>
						{activeView === 'orders' && runningOrders.length === 0 && (
							<div style={{
								display: 'flex', alignItems: 'center', gap: 8, color: '#888', fontSize: '1.1em', margin: '1em 0', flexDirection: 'column'
							}}>
								<Spinner size="small" />
								<span>{tLocal('comocat.visits.loading_running_orders')}</span>
							</div>
						)}
						{activeView === 'orders' && runningOrders.length > 0 && filteredRunningOrders?.length === 0 && (
							<div className="orders-empty-message">
								{tLocal('comocat.visits.no_running_orders')}<br />
								{/* <span className="orders-empty-date">{dayjs(orderDayFilter).locale('nl').format('dddd DD/MM/YYYY')}</span> */}
							</div>
						)}

					</div>
				</div>
				{showConfirm && (
					<ConfirmDialog
						open={showConfirm}
						title={tLocal('comocat.visits.take_over_title')}
						message={
                            (selectedCustomer?.openOrders && (Number(selectedCustomer?.openOrders) > 0))
                                ? tLocal('comocat.visits.take_over_confirm', selectedCustomer?.name ?? '')
                                : tLocal('comocat.visits.take_over_no_orders', selectedCustomer?.name ?? '')}
						confirmLabel={tLocal('comocat.visits.btn_confirm')}
						cancelLabel={tLocal('comocat.visits.btn_cancel')}
						onConfirm={async () => {
							if ((selectedCustomer?.openOrders && (Number(selectedCustomer?.openOrders) > 0)) ){
								setShowConfirm(false);
								await Api.setCustomer(selectedCustomer?.id, selectedCustomer);
								await comocatOrder(false);
								window.document.location = '/order';
							} else {
								setShowConfirm(false);
							}
						}}
						onCancel={() => setShowConfirm(false)}
					/>
				)}
			</RestrictedContent>
			<style>{`
                @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
                }
                `}</style>
		</>
	);
};
